import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { SignupStateService } from '../../services/signup-state.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import {
  COUNTRY_PHONE_OPTIONS,
  MOTIVOS_RELACION_COMERCIAL,
  PASOS_EMPRESA,
} from '../../constants/signup.constants';

/**
 * Pais del que sale todo e-RUT: lo emite el SII de Chile. Tiene que coincidir
 * con un `name` de COUNTRY_PHONE_OPTIONS, que es de donde salen las opciones
 * del select de pais.
 */
const PAIS_DEL_ERUT = 'Chile';

/**
 * Paso 11 (empresa): datos de la persona juridica, en dos sub-pasos.
 *
 *   1 de 2  Datos de la empresa. Razon social y RUT llegan del e-RUT (respaldados
 *           por su QR) y el telefono del OTP de WhatsApp ya verificado: los tres
 *           van en solo lectura. El giro llega sugerido desde la glosa de
 *           actividad del e-RUT, editable porque el QR no la respalda. El usuario
 *           agrega nombre de fantasia y fecha de constitucion.
 *   2 de 2  Direccion prellenada desde el e-RUT y editable, pais fijo en Chile,
 *           ciudad, comuna y relacion comercial a mano.
 *
 * Persiste en `data2` al cerrar el sub-paso 2, con el formulario completo, igual
 * que el registro de empresa del dashboard.
 */
@Component({
  selector: 'app-persona-juridica-step',
  templateUrl: './persona-juridica-step.component.html',
  styleUrls: ['./persona-juridica-step.component.sass'],
})
export class PersonaJuridicaStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  form!: FormGroup;
  countries = COUNTRY_PHONE_OPTIONS;
  motivosRelacion = MOTIVOS_RELACION_COMERCIAL;

  subStep: 1 | 2 = 1;
  loading = false;

  private userId: string | number | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    public signupState: SignupStateService,
    private companyService: CompanyRegistrationService
  ) {}

  ngOnInit(): void {
    // Prerrequisito: el e-RUT, de donde salen la razon social y el RUT
    if (!this.signupState.erutAnalysis) {
      this.signupState.goToStep(PASOS_EMPRESA.ERUT);
      return;
    }

    const authSub = this.store.select('auth').subscribe(({ user }: any) => {
      this.userId = user?.usuarioId ?? null;
    });
    this.subscriptions.push(authSub);

    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // ==================== FORMULARIO ====================

  private initForm(): void {
    const erut = this.signupState.erutAnalysis;
    const formData = this.signupState.currentState.formData;
    const saved = this.signupState.personaJuridica;

    this.form = this.fb.group({
      // Del e-RUT: verificados contra su QR, no se editan
      razonSocial: [
        { value: erut?.razonSocial || '', disabled: true },
        Validators.required,
      ],
      rut: [{ value: erut?.rut || '', disabled: true }, Validators.required],

      // Del OTP de WhatsApp ya verificado
      telefonoCodigo: [
        { value: saved?.telefonoCodigo || formData?.phoneCode || '', disabled: true },
      ],
      telefono: [
        { value: saved?.telefono || formData?.phone || '', disabled: true },
        Validators.required,
      ],

      // Sub-paso 1: el giro llega sugerido desde la glosa de actividad del
      // e-RUT. Ojo: el QR NO respalda ese campo, asi que se puede editar y no
      // se muestra como verificado. Nombre de fantasia y fecha de constitucion
      // no estan en el e-RUT y van a mano.
      nombreFantasia: [saved?.nombreFantasia || '', Validators.required],
      giroComercial: [
        saved?.giroComercial || erut?.glosaActividad || '',
        Validators.required,
      ],
      fechaConstitucion: [saved?.fechaConstitucion || '', Validators.required],

      // Sub-paso 2: la direccion principal del e-RUT si esta respaldada por el
      // QR, pero puede estar vencida —la sociedad se muda y no siempre lo
      // informa al SII— asi que llega prellenada y editable, no en solo lectura.
      // El pais es Chile por definicion: el e-RUT lo emite el SII. Ciudad y
      // comuna van a mano: el SII imprime la direccion en una sola cadena y
      // partirla para adivinar la comuna daria datos mal cortados.
      pais: [saved?.pais || PAIS_DEL_ERUT, Validators.required],
      ciudad: [saved?.ciudad || '', Validators.required],
      comuna: [saved?.comuna || '', Validators.required],
      direccion: [
        saved?.direccion || erut?.direccion || '',
        Validators.required,
      ],
      oficina: [saved?.oficina || ''],
      motivoRelacion2: [saved?.motivoRelacion2 || '', Validators.required],
    });
  }

  /** Campos que se validan en el sub-paso 1 antes de avanzar al 2. */
  private readonly camposSubStep1 = [
    'nombreFantasia',
    'giroComercial',
    'fechaConstitucion',
  ];

  get subStep1Valid(): boolean {
    return this.camposSubStep1.every((name) => !!this.form.get(name)?.valid);
  }

  // ==================== NAVEGACION ENTRE SUB-PASOS ====================

  goToSubStep2(): void {
    if (!this.subStep1Valid) {
      this.camposSubStep1.forEach((name) => this.form.get(name)?.markAsTouched());
      this.onError.emit('Completa los datos de la empresa para continuar.');
      return;
    }
    this.subStep = 2;
  }

  backToSubStep1(): void {
    this.subStep = 1;
  }

  // ==================== ENVIO ====================

  async onSubmit(): Promise<void> {
    if (this.loading) return;

    if (this.form.invalid) {
      this.markFormAsTouched();
      this.onError.emit('Completa todos los campos requeridos.');
      return;
    }

    if (!this.userId) {
      this.onError.emit(
        'No pudimos identificar la empresa. Recarga la pagina e intenta nuevamente.'
      );
      return;
    }

    this.loading = true;

    try {
      const values = this.form.getRawValue();

      // Guardar en el estado antes de la red, para no perder lo escrito
      this.signupState.setPersonaJuridica(values);

      await this.companyService.addDataToRegister(2, values, String(this.userId));

      this.onSuccess.emit('Datos de la empresa guardados');
      await this.signupState.nextStep();
    } catch (error: any) {
      this.onError.emit(
        error?.message || 'Error al guardar los datos. Intenta nuevamente.'
      );
    } finally {
      this.loading = false;
    }
  }

  private markFormAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && control.touched;
  }

  getFlagForCountry(countryName: string): string {
    const country = this.countries.find(
      (c) => c.name.toLowerCase() === countryName?.toLowerCase()
    );
    return country ? country.flag : 'chile.png';
  }

  get telefonoCompleto(): string {
    const code = this.form.get('telefonoCodigo')?.value || '';
    const phone = this.form.get('telefono')?.value || '';
    return `${code} ${phone}`.trim();
  }

  async goBack(): Promise<void> {
    if (this.subStep === 2) {
      this.backToSubStep1();
      return;
    }
    await this.signupState.prevStep();
  }
}
